import React, { useEffect, useState, useRef } from "react";

interface AutosaveCountdownProps {
  isActive: boolean;
  duration?: number; // Duration in milliseconds (default 5000ms)
  onComplete?: () => void;
}

export const AutosaveCountdown: React.FC<AutosaveCountdownProps> = ({
  isActive,
  duration = 6000,
  onComplete,
}) => {
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(duration / 1000);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      // Reset when not active
      setProgress(0);
      setTimeRemaining(duration / 1000);
      setShowCheckmark(false);
      startTimeRef.current = null;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    // Start the countdown
    startTimeRef.current = Date.now();
    setShowCheckmark(false);

    const animate = () => {
      if (!startTimeRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      const remaining = Math.max(0, Math.ceil((duration - elapsed) / 1000));

      setProgress(newProgress);
      setTimeRemaining(remaining);

      if (newProgress < 100) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setShowCheckmark(true);
        onComplete?.();
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, duration, onComplete]);

  if (!isActive) return null;

  // Circle parameters
  const size = 20;
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Invert so circle empties instead of fills
  const strokeDashoffset = (progress / 100) * circumference;

  // Show checkmark when countdown completes
  if (showCheckmark) {
    return (
      <div className="flex items-center gap-2 text-green-600">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="animate-scale-in"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="currentColor"
            opacity="0.2"
          />
          <path
            d="M6 10 L9 13 L14 7"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="text-sm font-medium">Saved!</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          opacity="0.2"
        />
        {/* Progress circle - empties as time runs out */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <span className="text-sm font-medium">Autosaving...</span>
    </div>
  );
};
